import React from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { SavedQuery } from '@/types';
import { Text, Title, TrevorIcon } from '@trevor/ui-components';
import { setDependantQueriesIds } from '@/features/dependantQueries/dependantQueriesSlice';
import { DEPENDANT_QUERIES_CLICKED } from '@/actions/constants';
import { formatTimestamp } from '@/util/helpers/time-helpers';
import { usePrefetchDependantSavedQueries } from '../hooks';
import {
  DEPENDANT_QUERIES_MODAL,
  START_FROM_SAVED_QUERY_DROPDOWN,
} from '../widgetRenderContext.constants';
import { useRenderMode } from '../hooks/useRenderMode';

const TEXT_ICON_SIZE = 10;

const Info = styled.div`
  flex-grow: 1;
`;

const DependantQueries = styled(Text)`
  & > span {
    border-bottom: 1px solid #666;
  }
`;

const dependantQueriesIcon = <TrevorIcon name="branch" size={TEXT_ICON_SIZE} />;

const RENDER_MODES_WITH_CLICKABLE_DEPENDANT_QUERIES = [
  DEPENDANT_QUERIES_MODAL,
  START_FROM_SAVED_QUERY_DROPDOWN,
];

export const SavedQueryInfo: React.FC<{
  onSelectQuery: F<SavedQuery>;
  query: SavedQuery;
}> = ({ query, onSelectQuery }) => {
  const dispatch = useDispatch();
  const mode = useRenderMode();
  const dependantQueries = [
    ...new Set([
      ...query.dependantQuerySeriesIds,
      ...query.dependantJoinSeriesIds,
    ]),
  ];

  const numberOfDependantQueries = dependantQueries.length;

  const prefetchDependantSavedQueries =
    usePrefetchDependantSavedQueries(dependantQueries);

  const handleDependantQueriesClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    dispatch(setDependantQueriesIds(dependantQueries));
    dispatch({
      type: DEPENDANT_QUERIES_CLICKED,
    });
  };

  const dependantQueriesText = `${numberOfDependantQueries} dependent ${
    numberOfDependantQueries === 1 ? 'query' : 'queries'
  }`;

  return (
    <Info onClick={() => onSelectQuery(query)}>
      <Title>{query.name}</Title>
      {numberOfDependantQueries > 0 &&
        (RENDER_MODES_WITH_CLICKABLE_DEPENDANT_QUERIES.includes(mode) ? (
          <DependantQueries
            icon={dependantQueriesIcon}
            onClick={handleDependantQueriesClick}
            onMouseEnter={prefetchDependantSavedQueries}
          >
            {dependantQueriesText}
          </DependantQueries>
        ) : (
          <Text icon={dependantQueriesIcon}>{dependantQueriesText}</Text>
        ))}
      <Text icon={<TrevorIcon name="rocket" size="sm" />}>
        {query.numberOfSteps} steps saved by {query.email},{' '}
        {formatTimestamp(query.createdAt)}
      </Text>
      {query.lastRefreshedAt && (
        <Text icon={<TrevorIcon name="refresh" size="sm" />}>
          Last refresh {formatTimestamp(query.lastRefreshedAt)},{' '}
          {query.numberOfResults
            ? `${query.numberOfResults} results`
            : 'with error'}
        </Text>
      )}
    </Info>
  );
};
